import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';

interface Product {
  id: number;
  name: string;
  description: string;
  unit: string;
  origin?: string;
  category: string;
}

type CategoryKey = 'seafood' | 'dairy' | 'spices' | 'protein' | 'fruits' | 'vegetables' | 'gluten';

interface CategoryInfo {
  title: string;
  description: string;
  icon: string;
}

const productData: Record<string, Product[]> = {
  seafood: [
    { id: 1, name: "Atlantic Salmon Fillet", description: "Premium Norwegian salmon, sustainably sourced", unit: "per lb", origin: "Norway", category: "seafood" },
    { id: 2, name: "Fresh Sea Bass", description: "Mediterranean sea bass, whole fish", unit: "per lb", origin: "Mediterranean", category: "seafood" },
    { id: 3, name: "Jumbo Shrimp", description: "U-10 Gulf shrimp, peeled and deveined", unit: "per lb", origin: "Gulf of Mexico", category: "seafood" },
    { id: 4, name: "Fin de Claire No. 3", description: "Premium French oysters, No. 3 size", unit: "per dozen", origin: "France", category: "seafood" },
    { id: 5, name: "Maine Lobster Tail", description: "Cold water lobster tails, 8-10oz", unit: "each", origin: "Maine", category: "seafood" },
    { id: 6, name: "Yellowfin Tuna", description: "Sashimi-grade yellowfin tuna loin", unit: "per lb", origin: "Pacific", category: "seafood" },
  ],
  dairy: [
    { id: 7, name: "Parmigiano Reggiano 24mo", description: "Aged Italian parmesan cheese", unit: "per lb", origin: "Italy", category: "dairy" },
    { id: 8, name: "French Brie de Meaux", description: "Authentic French soft cheese", unit: "per wheel", origin: "France", category: "dairy" },
    { id: 9, name: "Vermont Creamery Butter", description: "Cultured European-style butter", unit: "per lb", origin: "Vermont", category: "dairy" },
    { id: 10, name: "Buffalo Mozzarella", description: "Fresh Italian buffalo mozzarella", unit: "per 8oz", origin: "Italy", category: "dairy" },
    { id: 11, name: "Heavy Cream 40%", description: "Premium heavy whipping cream", unit: "per quart", category: "dairy" },
    { id: 12, name: "Aged Gruyère", description: "Swiss Gruyère aged 12 months", unit: "per lb", origin: "Switzerland", category: "dairy" },
  ],
  spices: [
    { id: 13, name: "Madagascar Vanilla Beans", description: "Grade A vanilla beans", unit: "per bean", origin: "Madagascar", category: "spices" },
    { id: 14, name: "Saffron Threads", description: "Premium Spanish saffron", unit: "per gram", origin: "Spain", category: "spices" },
    { id: 15, name: "Black Truffle Salt", description: "Sea salt infused with black truffle", unit: "per 4oz", origin: "France", category: "spices" },
    { id: 16, name: "Szechuan Peppercorns", description: "Authentic Chinese peppercorns", unit: "per 2oz", origin: "China", category: "spices" },
    { id: 17, name: "Himalayan Pink Salt", description: "Pure rock salt from Pakistan", unit: "per lb", origin: "Pakistan", category: "spices" },
    { id: 18, name: "Ceylon Cinnamon", description: "True cinnamon bark from Sri Lanka", unit: "per 4oz", origin: "Sri Lanka", category: "spices" },
  ],
  protein: [
    { id: 19, name: "Wagyu Beef Ribeye", description: "A5 Japanese Wagyu ribeye steak", unit: "per 12oz", origin: "Japan", category: "protein" },
    { id: 20, name: "Free-Range Chicken Breast", description: "Organic free-range chicken", unit: "per lb", category: "protein" },
    { id: 21, name: "Lamb Rack French-Cut", description: "New Zealand lamb rack, frenched", unit: "per rack", origin: "New Zealand", category: "protein" },
    { id: 22, name: "Duck Confit Legs", description: "Traditional French duck confit", unit: "per pair", origin: "France", category: "protein" },
    { id: 23, name: "Wild Boar Tenderloin", description: "Premium wild boar from Texas", unit: "per lb", origin: "Texas", category: "protein" },
    { id: 24, name: "Venison Medallions", description: "Farm-raised venison medallions", unit: "per lb", category: "protein" },
  ],
  fruits: [
    { id: 25, name: "Blood Oranges", description: "Sicilian blood oranges", unit: "per case", origin: "Sicily", category: "fruits" },
    { id: 26, name: "Meyer Lemons", description: "California Meyer lemons", unit: "per lb", origin: "California", category: "fruits" },
    { id: 27, name: "Fresh Figs", description: "Black Mission figs", unit: "per basket", origin: "California", category: "fruits" },
    { id: 28, name: "Rainier Cherries", description: "Premium Washington cherries", unit: "per lb", origin: "Washington", category: "fruits" },
    { id: 29, name: "Dragon Fruit", description: "Fresh white dragon fruit", unit: "each", origin: "Vietnam", category: "fruits" },
    { id: 30, name: "Passion Fruit", description: "Purple passion fruit", unit: "per dozen", origin: "Colombia", category: "fruits" },
  ],
  vegetables: [
    { id: 31, name: "Baby Artichokes", description: "Mediterranean baby artichokes", unit: "per case", origin: "California", category: "vegetables" },
    { id: 32, name: "Purple Top Turnips", description: "Heirloom purple top turnips", unit: "per bunch", category: "vegetables" },
    { id: 33, name: "Rainbow Carrots", description: "Multicolored heirloom carrots", unit: "per bunch", category: "vegetables" },
    { id: 34, name: "Fava Beans", description: "Fresh fava beans in pods", unit: "per lb", category: "vegetables" },
    { id: 35, name: "Micro Greens Mix", description: "Assorted micro greens", unit: "per 4oz", category: "vegetables" },
    { id: 36, name: "Romanesco Broccoli", description: "Italian Romanesco cauliflower", unit: "per head", origin: "Italy", category: "vegetables" },
  ],
  gluten: [
    { id: 37, name: "Italian 00 Flour", description: "Fine milled pasta and pizza flour", unit: "per 55lb bag", origin: "Italy", category: "gluten" },
    { id: 38, name: "Artisan Sourdough Starter", description: "100-year-old San Francisco starter", unit: "per 8oz", origin: "San Francisco", category: "gluten" },
    { id: 39, name: "Semolina Flour", description: "Durum wheat semolina for pasta", unit: "per 25lb bag", category: "gluten" },
    { id: 40, name: "French Bread Flour", description: "Traditional French bread flour", unit: "per 50lb bag", origin: "France", category: "gluten" },
    { id: 41, name: "Whole Wheat Berries", description: "Organic whole wheat berries", unit: "per 25lb bag", category: "gluten" },
    { id: 42, name: "Rye Flour", description: "Stone-ground dark rye flour", unit: "per 25lb bag", category: "gluten" },
  ],
};

const categoryInfo: Record<CategoryKey, CategoryInfo> = {
  seafood: {
    title: "Fresh Seafood",
    description: "Premium fresh and frozen seafood from trusted suppliers worldwide",
    icon: "🐟"
  },
  dairy: {
    title: "Artisan Dairy",
    description: "Finest cheeses, butters, and dairy products from renowned producers",
    icon: "🧀"
  },
  spices: {
    title: "Premium Spices",
    description: "Exotic spices and seasonings to elevate your culinary creations",
    icon: "🌶️"
  },
  protein: {
    title: "Premium Proteins",
    description: "Highest quality meats and poultry from ethical sources",
    icon: "🥩"
  },
  fruits: {
    title: "Exotic Fruits",
    description: "Seasonal and exotic fruits for the discerning chef",
    icon: "🍇"
  },
  vegetables: {
    title: "Fresh Vegetables",
    description: "Farm-fresh vegetables and specialty produce",
    icon: "🥬"
  },
  gluten: {
    title: "Artisan Flours & Grains",
    description: "Premium flours and grains for baking and cooking",
    icon: "🌾"
  }
};

const Products: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey>('seafood');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchMode, setSearchMode] = useState<'category' | 'all'>('category');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  // Stock images for carousel
  const carouselImages = [
    {
      url: "https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTJ8fGZydWl0c3xlbnwwfHwwfHx8MA%3D%3D",
      alt: "Premium ingredients"
    },
    {
      url: "https://images.unsplash.com/photo-1594731884638-8197c3102d1d?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8ZGFpcnl8ZW58MHx8MHx8fDA%3D",
      alt: "Premium ingredients"
    },
    {
      url: "https://images.unsplash.com/photo-1690983323540-d6e889c4b107?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8N3x8cmliZXllfGVufDB8fDB8fHww",
      alt: "Gourmet ingredients"
    },
    {
      url: "https://images.unsplash.com/photo-1554598286-ed7bfd1dedca?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8YXJ0aXNhbiUyMHdpbmUlMjBjZWxsYXJ8ZW58MHx8MHx8fDA%3D",
      alt: "Artisan wine cellar"
    },
    {
      url: "https://images.unsplash.com/photo-1625419301434-e6f9f0587b7c?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8ZmFybSUyMGZyZXNoJTIwcHJvZHVjZXxlbnwwfHwwfHx8MA%3D%3D",
      alt: "Farm fresh produce"
    },
    {
      url: "https://images.unsplash.com/photo-1560493676-04071c5f467b?w=900&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8ZmFybXxlbnwwfHwwfHx8MA%3D%3D",
      alt: "Farm fresh produce"
    }
  ];

  useEffect(() => {
    // Animate page entrance
    gsap.fromTo('.products-header', 
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }
    );
    gsap.fromTo('.carousel-container', 
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.6, delay: 0.2, ease: 'power2.out' }
    );
    gsap.fromTo('.category-nav', 
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.6, delay: 0.4, ease: 'power2.out' }
    );
    gsap.fromTo('.products-grid', 
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0, duration: 0.6, delay: 0.6, ease: 'power2.out' }
    );
  }, []);

  useEffect(() => {
    // Animate category change
    gsap.fromTo('.product-card', 
      { opacity: 0, scale: 0.9 },
      { opacity: 1, scale: 1, duration: 0.4, stagger: 0.1, ease: 'power2.out' }
    );
  }, [selectedCategory, searchTerm]);

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [carouselImages.length]);

  // Enhanced search functionality
  const getAllProducts = () => {
    return Object.values(productData).flat();
  };

  const filteredProducts = (() => {
    if (!searchTerm.trim()) {
      return productData[selectedCategory] || [];
    }

    const searchLower = searchTerm.toLowerCase();
    const productsToSearch = searchMode === 'all' ? getAllProducts() : (productData[selectedCategory] || []);
    
    return productsToSearch.filter(product =>
      product.name.toLowerCase().includes(searchLower) ||
      product.description.toLowerCase().includes(searchLower) ||
      (product.origin && product.origin.toLowerCase().includes(searchLower)) ||
      product.category.toLowerCase().includes(searchLower)
    );
  })();

  const clearSearch = () => {
    setSearchTerm('');
    setSearchMode('category');
  };

  const handleSearchModeChange = (mode: 'category' | 'all') => {
    setSearchMode(mode);
    if (mode === 'all') {
      setSelectedCategory('seafood'); // Reset to default category
    }
  };

  return (
    <div className="min-h-screen bg-background pt-32">
      {/* Page Header - Reduced spacing */}
      <div className="products-header section-container py-8">
        <div className="section-content">
          <div className="section-header">
            <h1 className="heading-xl heading-on-light mb-4">Our Premium Products</h1>
            <p className="text-body-lg text-on-light max-w-3xl mx-auto mb-6">
              Discover our carefully curated selection of premium ingredients sourced from the world's finest producers. 
              Each product is selected for its exceptional quality and taste to elevate your culinary creations.
            </p>
          </div>
        </div>
      </div>

      {/* Image Carousel */}
      <div className="carousel-container bg-background py-4">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl shadow-lg">
            <div className="relative h-64 md:h-80">
              <img
                src={carouselImages[currentImageIndex].url}
                alt={carouselImages[currentImageIndex].alt}
                className="w-full h-full object-cover transition-opacity duration-500"
              />
              <div className="absolute inset-0 bg-black/20"></div>
              
              {/* Carousel indicators */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {carouselImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section - Reduced spacing */}
      <div className="bg-background py-6">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {/* Search Mode Toggle */}
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => handleSearchModeChange('category')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                  searchMode === 'category'
                    ? 'bg-primary text-white shadow-lg'
                    : 'bg-white border border-accent text-text hover:bg-primary hover:text-white'
                }`}
              >
                Search in {categoryInfo[selectedCategory].title}
              </button>
              <button
                onClick={() => handleSearchModeChange('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                  searchMode === 'all'
                    ? 'bg-primary text-white shadow-lg'
                    : 'bg-white border border-accent text-text hover:bg-primary hover:text-white'
                }`}
              >
                Search All Products
              </button>
            </div>

            {/* Search Input */}
            <div className="relative">
              <input
                type="text"
                placeholder={searchMode === 'all' ? "Search all products by name, description, origin..." : `Search in ${categoryInfo[selectedCategory].title.toLowerCase()}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input w-full pl-12 pr-12"
              />
              <svg className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-accent hover:text-primary transition-colors"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>

            {/* Search Results Counter */}
            {searchTerm && (
              <div className="text-center">
                <p className="text-body-sm text-on-light">
                  {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} found
                  {searchMode === 'all' && filteredProducts.length > 0 && (
                    <span className="text-primary font-semibold">
                      {' '}across all categories
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Search Results - Display immediately under search bar when searching */}
      {searchTerm && (
        <div className="bg-background py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6">
              <div className="flex justify-center items-center space-x-4 mb-3">
                <span className="text-4xl">🔍</span>
                <h2 className="text-2xl font-bold text-text">Search Results</h2>
              </div>
              <p className="text-base text-text-accent max-w-2xl mx-auto">
                Found {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} matching "{searchTerm}"
                {searchMode === 'all' && ' across all categories'}
              </p>
            </div>

            {/* Search Results Grid */}
            {filteredProducts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="bg-white border border-accent rounded-lg p-4 hover:shadow-lg transition-all duration-300">
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <h3 className="text-lg font-bold text-text">{product.name}</h3>
                        {searchMode === 'all' && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
                            {categoryInfo[product.category as CategoryKey]?.icon} {categoryInfo[product.category as CategoryKey]?.title}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-text-accent">{product.description}</p>
                      
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-text-accent font-medium">Unit:</span>
                          <span className="text-xs text-primary font-semibold">{product.unit}</span>
                        </div>
                        {product.origin && (
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-text-accent font-medium">Origin:</span>
                            <span className="text-xs text-text-accent">{product.origin}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="mt-4">
                        {isHomePage ? (
                          <a href="#contact" className="bg-primary text-white w-full text-sm py-2 block text-center rounded-lg hover:bg-primary/90 transition-colors">
                            Request Quote
                          </a>
                        ) : (
                          <Link to="/#contact" className="bg-primary text-white w-full text-sm py-2 block text-center rounded-lg hover:bg-primary/90 transition-colors">
                            Request Quote
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-4xl mb-3">🔍</div>
                <h3 className="text-xl font-bold text-text mb-3">No products found</h3>
                <p className="text-base text-text-accent mb-4">
                  No products match your search for "{searchTerm}". Try different keywords or search all products.
                </p>
                <button
                  onClick={clearSearch}
                  className="bg-white border border-accent text-text px-6 py-3 rounded-lg hover:bg-primary hover:text-white transition-colors"
                >
                  Clear Search
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Category Navigation - Only show when not searching */}
      {!searchTerm && searchMode === 'category' && (
        <div className="category-nav bg-background py-4 border-b border-accent">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-3">
              {Object.entries(categoryInfo).map(([key, info]) => (
                <button
                  key={key}
                  onClick={() => setSelectedCategory(key as CategoryKey)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center space-x-2 ${
                    selectedCategory === key
                      ? 'bg-primary text-white shadow-lg'
                      : 'bg-white border border-accent text-text hover:bg-primary hover:text-white'
                  }`}
                >
                  <span className="text-lg">{info.icon}</span>
                  <span>{info.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Category Products - Only show when not searching */}
      {!searchTerm && (
        <div className="bg-background py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <div className="flex justify-center items-center space-x-4 mb-3">
                <span className="text-4xl">{categoryInfo[selectedCategory].icon}</span>
                <h2 className="text-2xl font-bold text-text">{categoryInfo[selectedCategory].title}</h2>
              </div>
              <p className="text-base text-text-accent max-w-2xl mx-auto">
                {categoryInfo[selectedCategory].description}
              </p>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredProducts.map((product) => (
                <div key={product.id} className="bg-white border border-accent rounded-lg p-4 hover:shadow-lg transition-all duration-300">
                  <div className="space-y-3">
                    <h3 className="text-lg font-bold text-text">{product.name}</h3>
                    <p className="text-sm text-text-accent">{product.description}</p>
                    
                    <div className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-text-accent font-medium">Unit:</span>
                        <span className="text-xs text-primary font-semibold">{product.unit}</span>
                      </div>
                      {product.origin && (
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-text-accent font-medium">Origin:</span>
                          <span className="text-xs text-text-accent">{product.origin}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4">
                      {isHomePage ? (
                        <a href="#contact" className="bg-primary text-white w-full text-sm py-2 block text-center rounded-lg hover:bg-primary/90 transition-colors">
                          Request Quote
                        </a>
                      ) : (
                        <Link to="/#contact" className="bg-primary text-white w-full text-sm py-2 block text-center rounded-lg hover:bg-primary/90 transition-colors">
                          Request Quote
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredProducts.length === 0 && (
              <div className="text-center py-8">
                <p className="text-base text-text-accent">No products found matching your search.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Call to Action - Reduced spacing */}
      <div className="cta-container-dark py-12">
        <div className="section-content">
          <div className="cta-content">
            <h2 className="heading-md heading-on-dark mb-4">Need Custom Products?</h2>
            <p className="text-body text-on-dark mb-6 max-w-2xl mx-auto">
              Can't find what you're looking for? Our team specializes in sourcing unique ingredients 
              and custom products for discerning chefs and restaurants.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact" className="btn-primary">
                Request Custom Quote
              </a>
              <a href="tel:+17215503241" className="btn-outline-light">
                Call Our Specialists
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
